import { useCrowdfundingProvider } from '#Context/index.js';
import {
  useActivityLogForm,
  useLogActivityModalProvider,
} from '#components/organisms/modals/LogActivityModal/LogActivityModalFlow';
import {
  PeerParticipant,
  PeerParticipantActivityLogEntry,
  useShowCampaignSettings,
} from '@givebacks/api/store';
import { Table } from 'core/Packages';
import { DateTime } from 'luxon';
import { Divider, MantineTable, notifications } from 'ui';
import { IconEdit, IconTrash } from 'ui/Icons';

import { useUpdateParticipantActivityLog } from '../../../../../api/new/campaigns/peers/update';

type ActivityLogTableRowProps = {
  activity: PeerParticipantActivityLogEntry;
  index: number;
  participant: PeerParticipant;
};

const ActivityLogTableRowActions = ({ activity, index, participant }: ActivityLogTableRowProps) => {
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { data: settings } = useShowCampaignSettings({ causeId, campaignId });
  const { openModalById, meta } = useLogActivityModalProvider({});
  const form = useActivityLogForm();

  const { mutate: updateParticipantActivityLog } = useUpdateParticipantActivityLog();

  const handleEditEntry = () => {
    form?.setValues({
      value: activity.value,
      date: DateTime.fromISO(activity.date).toJSDate(),
      index,
    });
    openModalById('base');
  };

  const handleDeleteEntry = () => {
    updateParticipantActivityLog(
      {
        campaignId: meta.campaignId,
        causeId: meta.causeId,
        participantId: participant.uuid,
        activityLog: participant.activity_log.filter((_, i) => i !== index),
      },
      {
        onSuccess: () => {
          notifications.show({
            variant: 'success',
            message: 'Entry removed',
          });
        },
        onError: () => {
          notifications.show({
            variant: 'error',
            message: 'Error removing entry',
          });
        },
      }
    );
  };

  if (settings?.isCampaignFinished) return null;

  return (
    <Table.RowAction>
      <Table.RowAction.Option
        label="Edit Logged Activity"
        icon={IconEdit}
        onClick={handleEditEntry}
      />
      <Divider mx="sm" />
      <Table.RowAction.Option
        label="Remove Entry"
        icon={IconTrash}
        destructive
        onClick={handleDeleteEntry}
      />
    </Table.RowAction>
  );
};

const ActivityLogTableRow = ({ activity, index, participant }: ActivityLogTableRowProps) => {
  const activityDate = DateTime.fromISO(activity.date).toLocaleString({
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
  return (
    <MantineTable.Tr>
      <MantineTable.Td>{activityDate}</MantineTable.Td>
      <MantineTable.Td>{activity.value}</MantineTable.Td>
      <MantineTable.Td w={40}>
        <ActivityLogTableRowActions activity={activity} index={index} participant={participant} />
      </MantineTable.Td>
    </MantineTable.Tr>
  );
};

export { ActivityLogTableRow };
